/**
 * Shared chat / messaging utilities used by every conversation UI on
 * CARTAL (MessagesPage — buyer/seller, DashMessages — business inbox,
 * and any future thread implementations).
 *
 * The functions here are intentionally *tiny and pure* so they can be
 * shared between the two existing chat containers without forcing a
 * larger refactor. They exist to prevent the "dancing messages" class
 * of bugs, which historically had four root causes:
 *
 *   1. Polling replaced the messages array with fresh object references
 *      every 5–15s, even when nothing had changed → every bubble
 *      remounted and any subtle transition was reset.
 *   2. The auto-scroll effect ran on EVERY messages-state change,
 *      including polls, so a user who scrolled up to read history
 *      would be yanked back to the bottom every poll cycle.
 *   3. Scrolls happened synchronously after setState, before the
 *      browser had laid out the new DOM, producing partial jumps.
 *   4. `setLoading(true)` was set on every silent background refresh,
 *      briefly replacing the conversation list with a skeleton.
 */
import { useCallback, useEffect, useLayoutEffect, useRef } from "react";

/** Return true iff two message lists are identical (by id + text/body).
 *  Callers use this to short-circuit setState so poll cycles produce
 *  zero React re-renders when nothing has actually changed. */
export function sameMessageList(prev, next) {
  if (prev === next) return true;
  if (!prev || !next) return false;
  if (prev.length !== next.length) return false;
  for (let i = 0; i < prev.length; i++) {
    const a = prev[i];
    const b = next[i];
    if (!a || !b) return false;
    if (a.id !== b.id) return false;
    // body (legacy /conversations API) or text (unified /platform/comms API)
    if ((a.text ?? a.body ?? "") !== (b.text ?? b.body ?? "")) return false;
  }
  return true;
}

/** Return true iff two conversation-list snapshots are identical for
 *  the fields the sidebar actually renders. Prevents the conv list
 *  from re-rendering every 8–15s. */
export function sameConvList(prev, next) {
  if (prev === next) return true;
  if (!prev || !next) return false;
  if (prev.length !== next.length) return false;
  for (let i = 0; i < prev.length; i++) {
    const a = prev[i];
    const b = next[i];
    if (!a || !b) return false;
    if (a.id !== b.id) return false;
    if ((a.last_message_at || a.updated_at || "") !== (b.last_message_at || b.updated_at || "")) return false;
    if ((a.last_message_preview || a.last_message || "") !== (b.last_message_preview || b.last_message || "")) return false;
    // unread counters — either shape used by the two APIs
    const aUnread = a.my_unread ?? a.unread ?? (typeof a.unread_by === "object" ? Object.values(a.unread_by).reduce((s, x) => s + Number(x || 0), 0) : 0);
    const bUnread = b.my_unread ?? b.unread ?? (typeof b.unread_by === "object" ? Object.values(b.unread_by).reduce((s, x) => s + Number(x || 0), 0) : 0);
    if (aUnread !== bUnread) return false;
  }
  return true;
}

/**
 * Smart auto-scroll hook.
 *
 * Attaches to a scroll container and:
 *  - remembers whether the user was near the bottom BEFORE new content arrived
 *  - only auto-scrolls to the bottom on updates if they were near the bottom,
 *    OR the last message is authored by the current user (i.e. they just sent
 *    one and expect to see it appear)
 *  - schedules the scroll inside a rAF so layout has stabilised
 *  - if the user is scrolled up reading older messages, the position is
 *    completely preserved (no more "dancing")
 *
 * Usage:
 *   const scrollRef = useSmartAutoScroll(messages, currentUserId);
 *   return <div ref={scrollRef}>{...bubbles}</div>;
 */
export function useSmartAutoScroll(items, myUserId, options = {}) {
  const { thresholdPx = 96 } = options;
  const containerRef = useRef(null);
  const wasNearBottomRef = useRef(true);
  const prevLenRef = useRef(0);
  const prevLastIdRef = useRef(null);

  // Before the DOM commits new items, record whether the user was near
  // the bottom of the scroll container. This runs synchronously before
  // paint so we capture the pre-update state.
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    wasNearBottomRef.current = distFromBottom <= thresholdPx;
  }, [items, thresholdPx]);

  // After paint, only scroll if it makes sense (see rules above).
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !items) return;

    const prevLen = prevLenRef.current;
    const prevLastId = prevLastIdRef.current;
    const nextLen = items.length;
    const nextLastId = items[nextLen - 1]?.id ?? null;

    // Nothing actually changed → do NOT touch scroll position. This is
    // the fix for the "yanks back to bottom every poll" bug.
    if (prevLen === nextLen && prevLastId === nextLastId) return;

    const grew = nextLen > prevLen;
    const lastMsg = items[nextLen - 1];
    const lastIsMine = lastMsg && myUserId && (lastMsg.sender_id === myUserId);
    const shouldScroll =
      prevLen === 0 ||               // first load
      lastIsMine ||                  // user just sent a message
      (grew && wasNearBottomRef.current); // new inbound msg AND user was following

    if (shouldScroll) {
      requestAnimationFrame(() => {
        // one more rAF to be safe with images / dynamic content that
        // may enlarge after mount
        requestAnimationFrame(() => {
          if (containerRef.current) {
            containerRef.current.scrollTop = containerRef.current.scrollHeight;
          }
        });
      });
    }

    prevLenRef.current = nextLen;
    prevLastIdRef.current = nextLastId;
  }, [items, myUserId]);

  return containerRef;
}

/**
 * Convenience updater used with useState setters:
 *   setMessages((prev) => idempotentUpdate(prev, next, sameMessageList));
 * Keeps the reference identity of `prev` when nothing changed so React
 * bails out of re-rendering.
 */
export function idempotentUpdate(prev, next, comparator) {
  return comparator(prev, next) ? prev : next;
}

/**
 * Debounce helper for silent poll updates so we never fire two setState
 * cycles back-to-back during the same tick.
 */
export function useLatestCallback(fn) {
  const ref = useRef(fn);
  useEffect(() => { ref.current = fn; }, [fn]);
  return useCallback((...args) => ref.current(...args), []);
}
